/**
 * =============================================================================
 * Componente: FormModalSection
 * Versão: 1.0.0
 * Release: 2026-05-02 — Helper de seção interna para modais premium
 *
 * Descrição:
 *   Padroniza a tipografia e o espaçamento dos blocos lógicos dentro de um
 *   FormModalShell. Reproduz o visual usado no TaskFormDialog
 *   (h3 + ícone primary + grid de campos com space-y-4).
 *
 * Uso:
 *   <FormModalSection icon={Gift} title="Recompensa">
 *     ...campos...
 *   </FormModalSection>
 * =============================================================================
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormModalSectionProps {
  /** Ícone (lucide-react) exibido ao lado do título da seção. */
  icon: LucideIcon;
  /** Título da seção. */
  title: string;
  /** Descrição secundária opcional, exibida abaixo do título. */
  description?: string;
  /** Conteúdo da seção. */
  children: React.ReactNode;
  /** Classe extra para o wrapper externo. */
  className?: string;
  /** Classe extra para o container dos filhos. */
  contentClassName?: string;
}

export function FormModalSection({
  icon: Icon,
  title,
  description,
  children,
  className,
  contentClassName,
}: FormModalSectionProps) {
  return (
    <div className={className}>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          {title}
        </h3>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      <div className={cn('space-y-4', contentClassName)}>{children}</div>
    </div>
  );
}
